import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  InlineStyleSnapshot,
  readInlineStyles,
  writeInlineStyles,
  getScrollPosition,
  setScrollPosition,
} from '../functions/style-utils';
import { BehaviorSubject } from 'rxjs';

export enum ElementLockType {
  ArrangementElement = 'arrangement-element',
  InvoiceHistoryTable = 'invoice-history-table',
}

export interface ElementLock {
  elementType: ElementLockType;
  status: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ScrollLockService {
  private bodySavedStyles: Record<string, InlineStyleSnapshot> | null = null;
  private rootSavedStyles: Record<string, InlineStyleSnapshot> | null = null;
  private bodySavedScroll = { x: 0, y: 0 };
  private bodyLockDepth = 0;
  private readonly bodyInlineProperties = [
    'padding-right',
    'overflow',
    'overflow-x',
    'overflow-y',
    'position',
    'top',
    'right',
    'bottom',
    'left',
    'width',
  ];

  private arrangementLockSource = new BehaviorSubject<ElementLock | null>(null);
  arrangementLock$ = this.arrangementLockSource.asObservable();

  private invoiceHistoryLockSource = new BehaviorSubject<ElementLock | null>(null);
  invoiceHistoryLock$ = this.invoiceHistoryLockSource.asObservable();

  private arrangementLockDepth = 0;
  private invoiceHistoryLockDepth = 0;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  // The ELEMENT-level locks below are live again for issue #163. They only ever set
  // overflow:hidden on the specific host element carrying [appScrollLock] (see the
  // directive), so wherever a wheel/touch scroll falls through a panel's dead zones
  // (padding, header, screen edge), the background element behind it cannot move.
  // They are safe: the 2025-11-01 rollback (6c176e99) was caused by bodyLock()'s
  // position:fixed on <body>, which broke every appendTo="body" PrimeNG overlay —
  // these element locks never touch <body> and never touch position.
  arrangementLock() {
    this.arrangementLockDepth += 1;
    if (this.arrangementLockDepth > 1) {
      return;
    }
    this.arrangementLockSource.next({
      elementType: ElementLockType.ArrangementElement,
      status: true,
    });
  }

  arrangementUnlock() {
    if (this.arrangementLockDepth === 0) {
      return;
    }
    this.arrangementLockDepth = Math.max(0, this.arrangementLockDepth - 1);
    if (this.arrangementLockDepth > 0) {
      return;
    }
    this.arrangementLockSource.next({
      elementType: ElementLockType.ArrangementElement,
      status: false,
    });
  }

  invoiceHistoryLock() {
    this.invoiceHistoryLockDepth += 1;
    if (this.invoiceHistoryLockDepth > 1) {
      return;
    }
    this.invoiceHistoryLockSource.next({
      elementType: ElementLockType.InvoiceHistoryTable,
      status: true,
    });
  }

  invoiceHistoryUnlock() {
    if (this.invoiceHistoryLockDepth === 0) {
      return;
    }
    this.invoiceHistoryLockDepth = Math.max(0, this.invoiceHistoryLockDepth - 1);
    if (this.invoiceHistoryLockDepth > 0) {
      return;
    }
    this.invoiceHistoryLockSource.next({
      elementType: ElementLockType.InvoiceHistoryTable,
      status: false,
    });
  }

  // bodyLock is live again for issue #163, but ONLY as an overflow lock. The original
  // version also set position:fixed on <body>, which re-parents the containing block for
  // every PrimeNG overlay using appendTo="body" and rendered dropdowns off-page — that is
  // what forced the 2025-11-01 rollback (commit 6c176e99). NEVER add position:fixed back.
  // Plain overflow:hidden on <body> does not affect containing blocks, so this lock is
  // safe to keep — but do NOT read the two [blockScroll] p-dialogs as proof that an
  // overflow lock stops iOS touch scrolling. PrimeNG 12's blockScroll is a strictly
  // weaker copy of this lock (body-only .p-overflow-hidden class, no root element, no
  // touch listeners — primeng-dialog.js:177) and those dialogs survive on iPad only
  // because their content is an <iframe>/<img> that swallows the pan in a nested
  // browsing context.
  //
  // It locks the ROOT element as well as <body> because body-overflow propagates to the
  // viewport only when the root's own overflow is `visible`, and no loaded stylesheet
  // puts an overflow rule on <html>. KNOW WHAT THIS LOCK IS AND IS NOT: the 2026-09-02
  // device test (93aae03c) proved both writes shipped and fired on the arrangement-open
  // path, and iPad Safari STILL panned the document while the URL bar was expanded.
  // That is documented WebKit behaviour, not a delivery failure: whenever the visual
  // viewport is smaller than the layout viewport, touch pans may scroll the document
  // despite overflow:hidden on both roots, because "scrolling is necessary to reveal
  // the browser UI" (WebKit bug 240860). So this lock is the desktop half — wheel,
  // scrollbar, keyboard, programmatic scrolling — plus the clamp once the chrome is
  // already collapsed. The iOS dead-zone touch pan is stopped one layer earlier, at
  // gesture initiation, by touch-action:none on the arrangement panel shell
  // (arrangement-detail.component.scss). Do not re-derive an overflow-based fix for it.
  bodyLock(): void {
    const body = this.document?.body;
    if (!body) {
      return;
    }
    this.bodyLockDepth += 1;
    if (this.bodyLockDepth > 1) {
      return;
    }
    const docEl = this.document.documentElement;
    const scrollbarWidth = Math.max(window.innerWidth - docEl.clientWidth, 0);
    this.bodySavedStyles = readInlineStyles(body, this.bodyInlineProperties);
    body.style.setProperty('overflow', 'hidden', 'important');
    // The root IS the viewport scroller. This freezes wheel/scrollbar/keyboard and
    // programmatic scrolling. It does NOT stop iOS touch pans while the URL bar is
    // expanded (WebKit 240860) — touch-action on the panel shell handles those.
    this.rootSavedStyles = readInlineStyles(docEl, ['overflow', 'overflow-x', 'overflow-y']);
    docEl.style.setProperty('overflow', 'hidden', 'important');
    // Compensate for the vanishing scrollbar so the page doesn't shift sideways on
    // systems with classic (non-overlay) scrollbars.
    if (scrollbarWidth > 0 && Number.isFinite(scrollbarWidth)) {
      body.style.setProperty('padding-right', `${scrollbarWidth}px`, 'important');
    }
  }

  bodyUnlock(): void {
    const body = this.document?.body;
    if (!body || this.bodyLockDepth === 0) {
      return;
    }
    this.bodyLockDepth = Math.max(0, this.bodyLockDepth - 1);
    if (this.bodyLockDepth > 0) {
      return;
    }
    if (this.bodySavedStyles) {
      writeInlineStyles(body, this.bodySavedStyles);
    }
    if (this.rootSavedStyles) {
      writeInlineStyles(this.document.documentElement, this.rootSavedStyles);
    }
    this.bodySavedStyles = null;
    this.rootSavedStyles = null;
  }
}
